import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import auth from "../../../utils/authentication";
import {getCachedImageUrl, setDarkMode} from "../../../utils/utils";
import {setDarkModeAction} from "../../users/actions";
import useUserPreferences from "../../user-app-config/hooks/use-user-preferences";
import Cap17driveIndex from "../../cap17drive/cap17driveIndex/cap17driveIndex";
import {MyProfile} from "../../profile";
import SquadflixDevicesPage from "../../cap17drive/squadflix-devices-page/squadflix-devices-page";
import SocketUtils, {randomBytes, useSocketEvents} from "../squadflix-tv-mode/socket-utils/socketUtils";
import SquadflixTvPlayer from "../squadflix-tv-mode/components/squadflix-tv-player/squadflix-tv-player";
import useTvSpatialNavigation from "../../cap17drive/squadflixTvTab/hooks/useTvSpatialNavigation";
import "./TvBasicLayout.scss";

type Props = {
    squadflixTvHook: any;
};

const TvBasicLayout: React.FC<Props> = ({squadflixTvHook}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const {updatePreference} = useUserPreferences();

    // Background Cast Receiver support
    const [castVideo, setCastVideo] = useState<any>(null);
    const [socketConnected, setSocketConnected] = useState(false);
    const [deviceInfo] = useState<any>(() => {
        try {
            const raw = localStorage.getItem("squadflixtv-deviceInfo");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });

    const deviceId = useMemo(() => {
        let dev = localStorage.getItem("squadflixtv-deviceId");
        if (!dev) {
            dev = randomBytes(32);
            localStorage.setItem("squadflixtv-deviceId", dev);
        }
        return dev;
    }, []);

    // Ensure dark mode is active in TV Mode
    useEffect(() => {
        setDarkMode(true);
        dispatch(setDarkModeAction(true));
    }, [dispatch]);

    // Connect socket with deviceId + user uid so TV can also act as Cast receptor in background
    useEffect(() => {
        const uid = deviceInfo?.uid || auth.currentUser?.uid;
        if (!uid || !deviceId) return;

        SocketUtils.connect({deviceId, uid})
            .then(() => setSocketConnected(true))
            .catch(() => {});
    }, [deviceId, deviceInfo?.uid]);

    const castEvents = useMemo(
        () => ({
            play_video_squadflix: (data: any) => {
                if (data?.video) {
                    setCastVideo(data.video);
                }
            }
        }),
        []
    );

    useSocketEvents({connected: socketConnected, events: castEvents});

    const handleTvBack = useCallback(() => {
        if (castVideo) {
            setCastVideo(null);
            return true;
        }
        return false;
    }, [castVideo]);

    useTvSpatialNavigation(true, handleTvBack);

    // Fullscreen state and handler for entire TV app
    const [isFullscreen, setIsFullscreen] = useState(
        () => !!(document.fullscreenElement || (document as any).webkitFullscreenElement)
    );

    useEffect(() => {
        const handleFsChange = () => {
            setIsFullscreen(!!(document.fullscreenElement || (document as any).webkitFullscreenElement));
        };
        document.addEventListener("fullscreenchange", handleFsChange);
        document.addEventListener("webkitfullscreenchange", handleFsChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFsChange);
            document.removeEventListener("webkitfullscreenchange", handleFsChange);
        };
    }, []);

    const toggleFullscreen = useCallback(async () => {
        try {
            const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
            if (!fsElement) {
                if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen();
                } else if ((document.documentElement as any).webkitRequestFullscreen) {
                    await (document.documentElement as any).webkitRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen();
                }
            }
        } catch (err) {
            console.error("Error toggling fullscreen", err);
        }
    }, []);

    // Global keyboard shortcut: F / f toggles fullscreen
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (
                e.target &&
                ((e.target as HTMLElement).tagName === "INPUT" ||
                    (e.target as HTMLElement).tagName === "TEXTAREA" ||
                    (e.target as HTMLElement).isContentEditable)
            ) {
                return;
            }
            if (e.key === "f" || e.key === "F") {
                e.preventDefault();
                toggleFullscreen();
            }
        };
        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, [toggleFullscreen]);

    const handleExitTvMode = async () => {
        await updatePreference("tvMode", false);
    };

    const isHomeActive =
        location.pathname === "/squadflix" ||
        location.pathname === "/squadflix/" ||
        location.pathname === "/squadflix/home";
    const isLiveActive = location.pathname.startsWith("/squadflix/live");
    const isSearchActive = location.pathname.startsWith("/squadflix/search-tmdb");
    const isConfigActive = location.pathname.startsWith("/my-profile/config");

    return (
        <div className="tv-basic-layout">
            {/* Top TV Navigation Bar */}
            <header className="tv-basic-layout__navbar" data-tv-row="tv-top-navbar">
                <button
                    type="button"
                    data-tv-focusable="true"
                    className="tv-basic-layout__brand"
                    onClick={() => navigate("/squadflix")}>
                    <span className="tv-basic-layout__brand-icon">
                        <i className="fa-solid fa-clapperboard"></i>
                    </span>
                    <span>SquadFlix</span>
                    <span className="tv-basic-layout__brand-badge">TV</span>
                </button>

                <nav className="tv-basic-layout__nav-links">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-basic-layout__nav-item ${isHomeActive ? "is-active" : ""}`}
                        onClick={() => navigate("/squadflix")}>
                        <i className="fa-solid fa-house"></i>
                        <span>Inicio</span>
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-basic-layout__nav-item ${isLiveActive ? "is-active" : ""}`}
                        onClick={() => navigate("/squadflix/live")}>
                        <i className="fa-solid fa-tower-broadcast"></i>
                        <span>En Vivo</span>
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-basic-layout__nav-item ${isSearchActive ? "is-active" : ""}`}
                        onClick={() => navigate("/squadflix/search-tmdb")}>
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <span>Buscar</span>
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-basic-layout__nav-item ${
                            location.pathname === "/tv-mode" ? "is-active" : ""
                        }`}
                        onClick={() => navigate("/tv-mode")}
                        title="Abrir pantalla de receptor Cast">
                        <i className="fa-brands fa-chromecast"></i>
                        <span>Modo Receptor</span>
                    </button>
                </nav>

                <div className="tv-basic-layout__right-actions">
                    {/* Fullscreen Button */}
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-basic-layout__fullscreen-btn ${isFullscreen ? "is-active" : ""}`}
                        onClick={toggleFullscreen}
                        title={isFullscreen ? "Salir de pantalla completa (F)" : "Pantalla completa (F)"}>
                        <i className={`fa-solid ${isFullscreen ? "fa-compress" : "fa-expand"}`}></i>
                        <span>{isFullscreen ? "Ventana" : "Pantalla completa"}</span>
                    </button>

                    {auth.currentUser && (
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-basic-layout__user-pill ${isConfigActive ? "is-active" : ""}`}
                            onClick={() => navigate("/my-profile/config")}
                            title="Ajustes y Preferencias">
                            {auth.currentUser.photoURL && (
                                <img
                                    src={getCachedImageUrl(auth.currentUser.photoURL)}
                                    alt={auth.currentUser.displayName || "Usuario"}
                                />
                            )}
                            <span>Ajustes</span>
                            <i className="fa-solid fa-gear"></i>
                        </button>
                    )}

                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-basic-layout__exit-btn"
                        onClick={handleExitTvMode}
                        title="Desactivar Modo TV y volver a SquadDisk normal">
                        <i className="fa-solid fa-desktop"></i>
                        <span>Salir Modo TV</span>
                    </button>
                </div>
            </header>

            {/* Main Scrollable Viewport for TV */}
            <main
                id="home__scrollContainer"
                className="tv-basic-layout__main squadflix-tv-main">
                <Routes>
                    <Route
                        path="/squadflix/*"
                        element={<Cap17driveIndex squadflixTvHook={squadflixTvHook} />}
                    />
                    <Route
                        path="/my-profile/config"
                        element={
                            <div className="container py-3">
                                <MyProfile />
                            </div>
                        }
                    />
                    <Route
                        path="/my-profile/devices"
                        element={
                            <div className="container py-3">
                                <SquadflixDevicesPage />
                            </div>
                        }
                    />
                    <Route path="*" element={<Navigate to="/squadflix" replace />} />
                </Routes>
            </main>

            {/* Background Cast Receiver Player Overlay */}
            {castVideo && (
                <div className="tv-basic-layout__cast-overlay">
                    <div className="tv-basic-layout__cast-overlay-bar" data-tv-row="tv-cast-bar">
                        <span>
                            <i className="fa-brands fa-chromecast pe-2 text-danger"></i>
                            Reproduciendo contenido enviado a esta TV
                        </span>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus="true"
                            className="tv-btn tv-btn--glass tv-btn--sm"
                            onClick={() => setCastVideo(null)}>
                            <i className="fa-solid fa-xmark pe-2"></i>Cerrar y volver a SquadFlix TV
                        </button>
                    </div>
                    <div style={{flex: 1, position: "relative"}}>
                        <SquadflixTvPlayer
                            video={castVideo}
                            deviceInfo={deviceInfo || {deviceId, uid: auth.currentUser?.uid}}
                            connected={socketConnected}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TvBasicLayout;
